import type { ContentfulClientApi } from 'contentful';
import { createClient } from 'contentful';

import { Configuration, EntitiesConfiguration } from './Configuration.ts';
import { FooterData, HeaderData, MaintenanceBannerData, ModalData } from './models';
import { TypeFooterSkeleton, TypeHeaderSkeleton, TypeMaintenanceBannerSkeleton, TypeModalSkeleton } from './models/cms';
import { transformFooter, transformHeader, transformMaintenanceBanners, transformModal } from './transformers';

export class ContentService {
  private client: ContentfulClientApi<undefined>;

  constructor(
    config: Configuration,
    private entities: EntitiesConfiguration
  ) {
    this.client = createClient({
      space: config.space,
      accessToken: config.accessToken,
      environment: config.environment,
    });
  }

  getFooter = async (): Promise<FooterData> => {
    const content = await this.client.withoutUnresolvableLinks.getEntries<TypeFooterSkeleton>({
      content_type: 'footer',
      'fields.name[match]': this.entities.footer,
      include: 10,
      limit: 1,
    });

    if (content?.items?.length !== 1) {
      throw new Error(`Expected exactly one item when fetching content: "${this.entities.footer}"`);
    }

    return transformFooter(content.items[0]);
  };

  getHeader = async (): Promise<HeaderData> => {
    const content = await this.client.withoutUnresolvableLinks.getEntries<TypeHeaderSkeleton>({
      content_type: 'header',
      'fields.name[match]': this.entities.header,
      include: 10,
      limit: 1,
    });

    if (content?.items?.length !== 1) {
      throw new Error(`Expected exactly one item when fetching content: "${this.entities.header}"`);
    }

    return transformHeader(content.items[0]);
  };

  getMaintenanceBanners = async (): Promise<MaintenanceBannerData[]> => {
    const content = await this.client.withoutUnresolvableLinks.getEntries<TypeMaintenanceBannerSkeleton>({
      content_type: 'maintenanceBanner',
    });
    return transformMaintenanceBanners(content.items);
  };

  getModal = async (): Promise<ModalData | undefined> => {
    const content = await this.client.withoutUnresolvableLinks.getEntries<TypeModalSkeleton>({
      content_type: 'modal',
      include: 10,
      limit: 1,
      'fields.active': true,
      order: ['-sys.updatedAt'],
    });

    if (content.items.length === 0) {
      return;
    }

    return transformModal(content.items[0]);
  };
}
